import { createFileRoute, useNavigate, Outlet, useMatches } from "@tanstack/react-router"
import { ProductDetailsView } from "@/components/dashboard/buyer/pages/FindSupplierPage"
import { SubmitRfqPage } from "@/components/dashboard/buyer/pages/SubmitRfqPage"
import type { RfqTarget } from "@/components/dashboard/buyer/pages/SubmitRfqPage"
import { useState } from "react"

export const Route = createFileRoute("/browse-products/products/$productId")({
  validateSearch: (search: Record<string, unknown>): {
    highlightVariantId?: string
    companyName?: string
    productName?: string
    source?: string
    category?: string
  } => ({
    highlightVariantId: (search.highlightVariantId as string) || undefined,
    companyName: (search.companyName as string) || undefined,
    productName: (search.productName as string) || undefined,
    source: (search.source as string) || undefined,
    category: (search.category as string) || undefined,
  }),
  component: ProductDetailRoute,
})

function ProductDetailRoute() {
  const { productId } = Route.useParams()
  const { highlightVariantId, companyName, source, category } = Route.useSearch()
  const navigate = useNavigate()
  const [rfqTarget, setRfqTarget] = useState<RfqTarget | null>(null)

  // Detect if a child route (variant detail) is active
  const matches = useMatches()
  const isVariantRouteActive = matches.some((m) =>
    m.routeId.includes("variants")
  )

  const handleBack = () => {
    if (category) {
      navigate({
        to: "/browse-products",
        search: { category, ...(source ? { source } : {}) },
      })
    } else if (source === "dashboard") {
      navigate({ to: "/dashboard" })
    } else if (source === "browse-products") {
      navigate({ to: "/browse-products" })
    } else if (window.history.length > 1) {
      window.history.back()
    } else {
      navigate({ to: "/explore" })
    }
  }

  // If the variant route is active, render the Outlet so TanStack Router can render it
  if (isVariantRouteActive) {
    return <Outlet />
  }

  if (rfqTarget) {
    return (
      <div className="max-w-6xl mx-auto pb-4">
        <SubmitRfqPage
          target={rfqTarget}
          isInline={false}
          onSuccess={() => {
            setRfqTarget(null)
            handleBack()
          }}
          onCancel={() => setRfqTarget(null)}
        />
      </div>
    )
  }

  return (
    <ProductDetailsView
      productId={productId}
      highlightVariantId={highlightVariantId}
      companyName={companyName}
      onBack={handleBack}
      onQuickInquiry={(product) => {
        setRfqTarget({
          type: "single-product",
          product: { ...product, id: product.productId || product.id },
          companyName,
        })
      }}
    />
  )
}
